import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  KYC_CERT_TYPE_OPTIONS,
  KYC_GENDER_OPTIONS,
  REAL_NAME_STATUS_OPTIONS,
  formatOptionalId,
  resolveContact,
  resolveCountryLabel,
  statusCode,
  toBirthdayInput,
} from './kycConstants';

const EDIT_STATUS_OPTIONS = REAL_NAME_STATUS_OPTIONS.filter((item) => item.value);

export default function KycEditModal({ open, row, onClose, onSaved }) {
  const [gender, setGender] = useState('');
  const [birthday, setBirthday] = useState('');
  const [lastName, setLastName] = useState('');
  const [realName, setRealName] = useState('');
  const [certType, setCertType] = useState('');
  const [identityNumber, setIdentityNumber] = useState('');
  const [realNameStatus, setRealNameStatus] = useState('');
  const [remark, setRemark] = useState('');
  const [verifyMessage, setVerifyMessage] = useState('');
  const [verifyOk, setVerifyOk] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (open && row) {
      setGender(row.gender != null ? String(row.gender) : '');
      setBirthday(toBirthdayInput(row.birthday));
      setLastName(row.lastName || '');
      setRealName(row.realName || '');
      setCertType(row.certType != null ? String(row.certType) : '');
      setIdentityNumber(row.identityNumber || '');
      setRealNameStatus(statusCode(row.realNameStatus));
      setRemark(row.remark || '');
      setVerifyMessage('');
      setVerifyOk(null);
      setError('');
    }
  }, [open, row?.memberId]);

  if (!open || !row) {
    return null;
  }

  const canSubmit = gender && identityNumber.trim() && realNameStatus;

  async function handleVerify() {
    const number = identityNumber.trim();
    if (!number) {
      setVerifyMessage('請先輸入證件號碼');
      setVerifyOk(false);
      return;
    }
    setVerifying(true);
    setVerifyMessage('');
    setVerifyOk(null);
    try {
      const used = await adminApi.kycNumberVerification(number, row.memberId);
      if (used) {
        setVerifyMessage('該證件號碼已被使用！');
        setVerifyOk(false);
      } else {
        setVerifyMessage('該證件號碼沒有使用過！');
        setVerifyOk(true);
      }
    } catch (err) {
      setVerifyMessage(err.message || '驗證失敗');
      setVerifyOk(false);
    } finally {
      setVerifying(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) {
      setError('請填寫必填欄位');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await adminApi.kycUpdate({
        memberId: row.memberId,
        gender: Number(gender),
        birthday: birthday || undefined,
        lastName: lastName.trim() || undefined,
        realName: realName.trim() || undefined,
        certType: certType ? Number(certType) : undefined,
        identityNumber: identityNumber.trim(),
        realNameStatus,
        remark: remark.trim() || undefined,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || '提交失敗');
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setGender(row.gender != null ? String(row.gender) : '');
    setBirthday(toBirthdayInput(row.birthday));
    setLastName(row.lastName || '');
    setRealName(row.realName || '');
    setCertType(row.certType != null ? String(row.certType) : '');
    setIdentityNumber(row.identityNumber || '');
    setRealNameStatus(statusCode(row.realNameStatus));
    setRemark(row.remark || '');
    setVerifyMessage('');
    setVerifyOk(null);
    setError('');
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>修改 [{formatOptionalId(row.memberId)}]</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body">
            <div className="product-detail-grid">
              <p className="page-desc">用戶ID：{formatOptionalId(row.memberId)}</p>
              <p className="page-desc">國家：{resolveCountryLabel(row)}</p>
              <div className="kyc-id-images">
                <div className="product-detail-row kyc-id-image-row">
                  <span className="product-detail-row__label">證件(正面)</span>
                  <span className="product-detail-row__value">
                    {row.identityImageFront ? (
                      <img src={resolveAdminMediaUrl(row.identityImageFront)} alt="證件正面" className="kyc-id-image" />
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <div className="product-detail-row kyc-id-image-row">
                  <span className="product-detail-row__label">證件(反面)</span>
                  <span className="product-detail-row__value">
                    {row.identityImageBack ? (
                      <img src={resolveAdminMediaUrl(row.identityImageBack)} alt="證件反面" className="kyc-id-image" />
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              </div>
              <p className="page-desc">郵箱/手機號：{resolveContact(row)}</p>
            </div>

            <div className="form-grid">
              <div className="form-field">
                <label>
                  <span className="required">*</span> 性別
                </label>
                <div className="radio-row">
                  {KYC_GENDER_OPTIONS.map((item) => (
                    <label key={item.value}>
                      <input
                        type="radio"
                        name="kycEditGender"
                        value={String(item.value)}
                        checked={gender === String(item.value)}
                        onChange={() => setGender(String(item.value))}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="kycEditBirthday">生日</label>
                <input
                  id="kycEditBirthday"
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="kycEditLastName">姓</label>
                <input
                  id="kycEditLastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="kycEditRealName">名</label>
                <input
                  id="kycEditRealName"
                  type="text"
                  value={realName}
                  onChange={(e) => setRealName(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="kycEditCertType">證件類型</label>
                <select
                  id="kycEditCertType"
                  value={certType}
                  onChange={(e) => setCertType(e.target.value)}
                >
                  <option value="">請選擇</option>
                  {KYC_CERT_TYPE_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field kyc-verify-field">
                <label htmlFor="kycEditIdentityNumber">
                  <span className="required">*</span> 證件號碼
                </label>
                <div className="kyc-verify-row">
                  <input
                    id="kycEditIdentityNumber"
                    type="text"
                    value={identityNumber}
                    onChange={(e) => {
                      setIdentityNumber(e.target.value);
                      setVerifyMessage('');
                      setVerifyOk(null);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleVerify}
                    disabled={verifying}
                  >
                    {verifying ? '驗證中…' : '驗證'}
                  </button>
                </div>
                {verifyMessage && (
                  <p className={verifyOk ? 'kyc-verify-msg kyc-verify-msg--ok' : 'kyc-verify-msg kyc-verify-msg--fail'}>
                    {verifyMessage}
                  </p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="kycEditRemark">備註</label>
                <input
                  id="kycEditRemark"
                  type="text"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="kycEditStatus">認證狀態</label>
                <select
                  id="kycEditStatus"
                  value={realNameStatus}
                  onChange={(e) => setRealNameStatus(e.target.value)}
                >
                  <option value="">請選擇</option>
                  {EDIT_STATUS_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && <p className="form-error">{error}</p>}
          </div>
          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className={`btn btn-success${canSubmit ? '' : ' is-disabled'}`} disabled={submitting || !canSubmit}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-warning" onClick={handleReset}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
